// Custom groove coordinates use the exterior view: X from left, Y from top.
// Axes describe centre lines; cutter coordinates use the geometry's bottom-left datum.
export const MAX_GROOVE_ELEMENTS = 40;
export const MAX_GROOVE_CUTS = 80;
export const grooveSpan = (startMm = 120, endMm = startMm) => ({ mode: 'edges', startMm, endMm });
export const groovePosition = (ratio = 0.5) => ({ mode: 'fixed', sizeMm: 0, anchor: { edge: 'ratio', ratio, offsetMm: 0 } });
const finite = (n, label, min = -4000, max = 4000) => {
  if (!Number.isFinite(n) || n < min || n > max) throw new Error(`${label} must be a finite number from ${min} to ${max}.`);
  return n;
};

function readAxis(axis, point, label) {
  if (!axis || !['fixed', 'edges', 'relative'].includes(axis.mode)) throw new Error(`${label}: choose a sizing rule.`);
  if (point && (axis.mode !== 'fixed' || axis.sizeMm !== 0)) throw new Error(`${label}: a line position cannot have a size.`);
  if (axis.mode === 'edges') return { mode: 'edges',
    startMm: finite(axis.startMm, `${label} start distance`, 0), endMm: finite(axis.endMm, `${label} end distance`, 0) };
  const anchor = axis.anchor;
  if (!anchor || !['start', 'end', 'center', 'ratio'].includes(anchor.edge)) throw new Error(`${label}: choose an anchor.`);
  return { mode: axis.mode,
    ...(axis.mode === 'fixed' ? { sizeMm: finite(axis.sizeMm, `${label} size`, point ? 0 : 0.1) }
      : { ratio: finite(axis.ratio, `${label} size ratio`, 0.001, 1) }),
    anchor: { edge: anchor.edge, offsetMm: finite(anchor.offsetMm, `${label} offset`),
      ...(anchor.edge === 'ratio' ? { ratio: finite(anchor.ratio, `${label} position ratio`, 0, 1) } : {}) } };
}

export function readGrooveElements(elements) {
  if (!Array.isArray(elements) || elements.length > MAX_GROOVE_ELEMENTS) throw new Error(`Use up to ${MAX_GROOVE_ELEMENTS} groove elements.`);
  const ids = new Set();
  let cuts = 0;
  return elements.map((element, i) => {
    const label = `Groove element ${i + 1}`;
    if (!element || typeof element.id !== 'string' || !element.id || element.id.length > 100 || ids.has(element.id)) {
      throw new Error(`${label} needs a unique ID.`);
    }
    ids.add(element.id);
    if (!['horizontal', 'vertical', 'rectangle'].includes(element.type)) throw new Error(`${label}: choose a line or rectangle.`);
    cuts += element.type === 'rectangle' ? 4 : 1;
    if (cuts > MAX_GROOVE_CUTS) throw new Error(`Use up to ${MAX_GROOVE_CUTS} groove segments.`);
    return { id: element.id, type: element.type,
      x: readAxis(element.x, element.type === 'vertical', `${label} horizontal axis`),
      y: readAxis(element.y, element.type === 'horizontal', `${label} vertical axis`) };
  });
}

export function resolveGrooveAxis(axis, extent) {
  if (axis.mode === 'edges') return { start: axis.startMm, size: extent - axis.startMm - axis.endMm };
  const size = axis.mode === 'relative' ? extent * axis.ratio : axis.sizeMm;
  const { edge, offsetMm, ratio } = axis.anchor;
  const start = edge === 'start' ? offsetMm : edge === 'end' ? extent - size - offsetMm
    : (extent - size) * (edge === 'center' ? 0.5 : ratio) + offsetMm;
  return { start, size };
}

export function resolveCustomGrooves(elements, widthMm, heightMm, grooveWidthMm) {
  const cuts = [], issues = [], objects = [];
  const half = grooveWidthMm / 2;
  readGrooveElements(elements).forEach((element, index) => {
    const x = resolveGrooveAxis(element.x, widthMm), y = resolveGrooveAxis(element.y, heightMm);
    const object = { ...element, xMm: x.start, topMm: y.start, widthMm: x.size, heightMm: y.size };
    objects.push(object);
    if ((element.type !== 'vertical' && x.size <= 0) || (element.type !== 'horizontal' && y.size <= 0)) {
      issues.push(`Element ${index + 1}: edge distances leave no room. Reduce the distances or increase the leaf size.`);
      return;
    }
    const add = (direction, left, top, w, h) => {
      const cut = { xMm: left, yMm: heightMm - top - h, widthMm: w, heightMm: h, direction, elementId: element.id };
      cuts.push(cut);
      if (left < -0.001 || top < -0.001 || left + w > widthMm + 0.001 || top + h > heightMm + 0.001) {
        const message = `Element ${index + 1}: groove extends beyond the leaf. Include half the groove width in edge clearances.`;
        if (!issues.includes(message)) issues.push(message);
      }
    };
    if (element.type === 'horizontal') add('horizontal', x.start, y.start - half, x.size, grooveWidthMm);
    if (element.type === 'vertical') add('vertical', x.start - half, y.start, grooveWidthMm, y.size);
    if (element.type === 'rectangle') {
      // Horizontal cuts cover the corner squares; vertical cuts meet their centre lines.
      add('horizontal', x.start - half, y.start - half, x.size + grooveWidthMm, grooveWidthMm);
      add('horizontal', x.start - half, y.start + y.size - half, x.size + grooveWidthMm, grooveWidthMm);
      add('vertical', x.start - half, y.start, grooveWidthMm, y.size);
      add('vertical', x.start + x.size - half, y.start, grooveWidthMm, y.size);
    }
  });
  return { cuts, objects, issues };
}

export function grooveWindowWarnings(cuts, openings, overlapMm = 0) {
  return openings.flatMap((o, i) => cuts.some(c => c.xMm < o.xMm + o.widthMm + overlapMm &&
    c.xMm + c.widthMm > o.xMm - overlapMm && c.yMm < o.yMm + o.heightMm + overlapMm &&
    c.yMm + c.heightMm > o.yMm - overlapMm)
    ? [`Grooves cross window ${i + 1} or its cassette. Those sections are hidden by the opening / trim.`] : []);
}

// Moves the element without changing its sizing or anchoring rule.
export function moveGrooveAxis(axis, delta) {
  if (axis.mode === 'edges') return { ...axis, startMm: axis.startMm + delta, endMm: axis.endMm - delta };
  return { ...axis, anchor: { ...axis.anchor, offsetMm: axis.anchor.offsetMm + (axis.anchor.edge === 'end' ? -delta : delta) } };
}

export function mirrorGrooveAxis(axis) {
  if (axis.mode === 'edges') return { ...axis, startMm: axis.endMm, endMm: axis.startMm };
  const a = axis.anchor;
  return { ...axis, anchor: { ...a, edge: a.edge === 'start' ? 'end' : a.edge === 'end' ? 'start' : a.edge,
    offsetMm: ['center', 'ratio'].includes(a.edge) ? -a.offsetMm : a.offsetMm,
    ...(a.edge === 'ratio' ? { ratio: 1 - a.ratio } : {}) } };
}

// Keeps the old equal-clear-panel behaviour at every leaf size, not just at conversion.
export function customGroovesFromLegacy(settings) {
  if (settings.mode === 'custom') return { ...settings, elements: readGrooveElements(settings.elements) };
  const { count = 0, direction = 'horizontal', widthMm = 10 } = settings;
  return { ...settings, mode: 'custom', elements: Array.from({ length: count }, (_, i) => {
    const ratio = (i + 1) / (count + 1);
    const point = groovePosition(ratio);
    point.anchor.offsetMm = widthMm * (ratio - 0.5);
    return { id: `legacy-${i + 1}`, type: direction,
      x: direction === 'horizontal' ? grooveSpan(0) : point,
      y: direction === 'vertical' ? grooveSpan(0) : point };
  }) };
}
